import {
  MESSAGE_TYPE,
  CONVERSATION_PRIORITY_ORDER,
} from 'shared/constants/messages';
import { applyPageFilters, applyCustomFilters } from './helpers';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => Number(conversation.id) === Number(selectedChatId));

// getters
const getters = {
  getAllConversations: ({ allConversations, chatSortFilter }) => {
    const comparator = {
      latest: (a, b) => b.last_activity_at - a.last_activity_at,
      latest_first: (a, b) => b.last_activity_at - a.last_activity_at,
      latest_old: (a, b) => a.last_activity_at - b.last_activity_at,
      sort_on_created_at: (a, b) => a.created_at - b.created_at,
      sort_on_priority: (a, b) => {
        return (
          CONVERSATION_PRIORITY_ORDER[a.priority] -
          CONVERSATION_PRIORITY_ORDER[b.priority]
        );
      },
    };

    return allConversations.sort(comparator[chatSortFilter] ?? comparator['latest']);
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: (_state, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { attachments } = selectedChat;
    return attachments || [];
  },
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isIncomingOrOutgoing =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.INCOMING;
      if (email.from && isIncomingOrOutgoing) {
        return true;
      }
      return false;
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
  },
  getCustomViewChats: (_state, _, __, rootGetters) => () => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;

      return isAssignedToMe;
    });
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getUnAssignedChats: _state => activeFilters => {
    return _state.allConversations.filter(conversation => {
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return isUnAssigned && shouldFilter;
    });
  },
  getAllStatusChats: _state => activeFilters => {
    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return shouldFilter;
    });
  },
  getChatListLoadingByGroupedBoard: _state => groupedKey => {
    return _state.listLoadingByGroupedBoard[groupedKey] || false;
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getConversationListNextRefreshAt: ({ conversationListNextRefreshAt }) =>
    conversationListNextRefreshAt,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getCampaignFilter: ({ campaignFilter }) => campaignFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationByPrimaryKey: _state => conversationId => {
    return _state.allConversations.find(
      value => value.primary_key === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },
  getFilteredChatsByCustomFolder: (_state, _, __, rootGetters) => filters => {
    return _state.allConversations.filter(conversation => {
      const shouldApplyCustomFilters = applyCustomFilters(conversation, filters);
      return shouldApplyCustomFilters;
    });
  },
  hasMessageIdInConversations: _state => messageId => {
    return _state.allConversations.filter(conversation => {
      return conversation.messages?.filter(message => message.id === messageId).length > 0;
    }).length > 0;
  },
  getMessageUIFlags($state) {
    return $state.messageUiFlags;
  },
};

export default getters;
